import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { basicInfoSchema, type BasicInfoInput } from 'shared'
import Input from '../ui/Input'
import RadioGroup from '../ui/RadioGroup'
import Button from '../ui/Button'
import type { PatientRecord } from 'shared'

interface Step1Props {
  initialData?: Partial<PatientRecord>
  onNext: (data: Partial<PatientRecord>) => void
}

const Step1BasicInfo = ({ initialData, onNext }: Step1Props) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<BasicInfoInput>({
    mode: 'onBlur',
    resolver: zodResolver(basicInfoSchema),
    defaultValues: initialData?.basicInfo,
  })

  const onSubmit = (data: BasicInfoInput) => {
    onNext({ basicInfo: data })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-5xl mx-auto">
      <h2 className="text-xl font-medium text-neutral-800 mb-6">基本情報</h2>

      <div className="space-y-5">
        {/* 名前（漢字） */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            {...register('lastNameKanji')}
            label="姓（漢字）"
            placeholder="山田"
            error={errors.lastNameKanji?.message}
            required
          />
          <Input
            {...register('firstNameKanji')}
            label="名（漢字）"
            placeholder="太郎"
            error={errors.firstNameKanji?.message}
            required
          />
        </div>

        {/* 名前（カナ） */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            {...register('lastNameKana')}
            label="姓（カナ）"
            placeholder="ヤマダ"
            error={errors.lastNameKana?.message}
            helperText="全角カタカナで入力してください"
            required
          />
          <Input
            {...register('firstNameKana')}
            label="名（カナ）"
            placeholder="タロウ"
            error={errors.firstNameKana?.message}
            helperText="全角カタカナで入力してください"
            required
          />
        </div>

        {/* 電話番号 */}
        <Input
          {...register('phoneNumber')}
          label="電話番号"
          type="tel"
          placeholder="09012345678"
          error={errors.phoneNumber?.message}
          helperText="ハイフンなしで入力してください"
          required
        />

        {/* メールアドレス */}
        <Input
          {...register('email')}
          label="メールアドレス"
          type="email"
          placeholder="example@example.com"
          error={errors.email?.message}
        />

        {/* 性別 */}
        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <RadioGroup
              label="性別"
              name="gender"
              options={[
                { value: '男性', label: '男性' },
                { value: '女性', label: '女性' },
                { value: 'その他', label: 'その他' },
              ]}
              value={field.value}
              onChange={field.onChange}
              error={errors.gender?.message}
              required
            />
          )}
        />

        {/* 生年月日 */}
        <Input
          {...register('birthday')}
          label="生年月日"
          type="date"
          min="1900-01-01"
          max={new Date().toISOString().split('T')[0]}
          error={errors.birthday?.message}
          required
        />
      </div>

      <div className="mt-8 flex justify-end">
        <Button type="submit" variant="primary" size="large" className="min-w-[200px]">
          次へ進む
        </Button>
      </div>
    </form>
  )
}

export default Step1BasicInfo
